import React from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { BuildingOfficeIcon, PhoneIcon } from '@heroicons/react/24/outline';

/**
 * View Business Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.business - Business data to display
 * @param {Function} props.onEdit - Edit handler
 */
const ViewBusinessModal = ({ isOpen, onClose, business, onEdit }) => {
  if (!business) return null;

  // Parse address if it's a string, or use individual fields
  const getAddressField = (fieldName) => {
    if (business[fieldName]) {
      return business[fieldName];
    }
    // If address is a string, try to extract (basic parsing)
    if (business.address && typeof business.address === 'string') {
      // This is a fallback - ideally use individual fields
      return null;
    }
    return null;
  };

  const streetName = getAddressField('street_name') || business.street_name || '';
  const purok = getAddressField('purok') || business.purok || '';
  const barangay = business.barangay || getAddressField('barangay') || '';
  const municipality = getAddressField('municipality') || business.municipality || '';
  const province = getAddressField('province') || business.province || '';
  const zipCode = getAddressField('zip_code') || business.zip_code || '';

  // Get nature of business - could be business_category or a description
  const natureOfBusiness = business.business_category || 
    business.nature_of_business || 
    `Retail sale of basic consumer goods, groceries, and household essentials through a neighborhood convenience store.`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span>Business Profile</span>
          {business.business_code && (
            <span className="inline-flex items-center px-3 py-1 rounded-lg bg-blue-600 text-white text-sm font-medium">
              {business.business_code}
            </span>
          )}
        </div>
      }
      size="2xl"
    >
      <div className="space-y-6">
        {/* Business Information Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Business Name</p>
              <p className="text-base font-medium text-gray-900">{business.business_name || 'N/A'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Business Type</p>
              <p className="text-base font-medium text-gray-900">
                {typeof business.business_type === 'object' && business.business_type?.name 
                  ? business.business_type.name 
                  : business.business_type || 'N/A'}
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-1">Nature of Business</p>
            <p className="text-base font-medium text-gray-900">{natureOfBusiness}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Owner</p>
              <p className="text-base font-medium text-gray-900">
                {typeof business.owner === 'object' && business.owner?.name 
                  ? business.owner.name 
                  : business.owner || business.owner_name || 'N/A'}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Classification</p>
              <p className="text-base font-medium text-gray-900">{business.classification || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Business Address Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <PhoneIcon className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Business Address</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Street Name</p>
              <p className="text-base font-medium text-gray-900">{streetName || 'N/A'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Purok</p>
              <p className="text-base font-medium text-gray-900">{purok || 'N/A'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Barangay</p>
              <p className="text-base font-medium text-gray-900">{barangay || 'N/A'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Municipality</p>
              <p className="text-base font-medium text-gray-900">{municipality || 'N/A'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Province</p>
              <p className="text-base font-medium text-gray-900">{province || 'N/A'}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">ZIP Code</p>
              <p className="text-base font-medium text-gray-900">{zipCode || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {onEdit && (
            <Button variant="primary" onClick={() => onEdit(business)}>
              Edit
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ViewBusinessModal;
